/**
 * Accurate browser geolocation.
 *
 * Problem: navigator.geolocation.getCurrentPosition() often returns the FIRST
 * fix the browser produces — frequently a coarse network/Wi-Fi/IP fix (accuracy
 * of hundreds of metres to kilometres) that arrives before GPS warms up. That
 * coarse fix reverse-geocodes to a nearby-but-wrong address ("1 in 6 times it's
 * very wrong"). Such fixes give tell-tale low-precision coordinates like
 * "15.8164, 74.4911".
 *
 * Solution: watchPosition() and keep the best-accuracy reading. Resolve early
 * once accuracy is within `desiredAccuracy`. Otherwise — the common desktop case,
 * where the browser emits a single coarse Wi-Fi/IP fix and never improves — wait a
 * short `settleTime` after the FIRST fix for a sharper one, then resolve with the
 * best. `timeout` is the hard ceiling. We never reject purely on poor accuracy
 * (desktops have no GPS and are always network-based), but `coords.accuracy` is
 * exposed so callers can warn the user.
 */

export type AccuratePositionOptions = {
  /** Stop as soon as a fix at or below this accuracy (metres) arrives. Default 50. */
  desiredAccuracy?: number;
  /** Hard ceiling: give up waiting after this many ms; resolve with best so far. Default 15000. */
  timeout?: number;
  /** After the first fix, wait at most this long for a better one before resolving. Default 3000. */
  settleTime?: number;
};

// Alias the browser-global type once (eslint's no-undef doesn't know this DOM interface).
// eslint-disable-next-line no-undef
type GeoPosition = GeolocationPosition;

export function getAccuratePosition(
  options: AccuratePositionOptions = {}
): Promise<GeoPosition> {
  const desiredAccuracy = options.desiredAccuracy ?? 50;
  const timeout = options.timeout ?? 15000;
  const settleTime = options.settleTime ?? 3000;

  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }

    let best: GeoPosition | null = null;
    let settled = false;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;

    const finish = (err?: unknown) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      clearTimeout(timer);
      if (settleTimer) clearTimeout(settleTimer);
      if (best) resolve(best);
      else reject(err instanceof Error ? err : new Error('Unable to determine location'));
    };

    const timer = setTimeout(() => finish(), timeout);

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos;
        // Good enough — stop now.
        if (pos.coords.accuracy <= desiredAccuracy) {
          finish();
          return;
        }
        // First usable (but coarse) fix: give GPS a brief window to deliver a
        // sharper one, then resolve with the best instead of waiting out the full
        // timeout. Without this, desktops (single coarse fix, never improves) would
        // spin until `timeout`.
        if (!settleTimer) {
          settleTimer = setTimeout(() => finish(), settleTime);
        }
      },
      (err) => {
        // Only fail if we never got any fix; otherwise keep the best we have.
        if (!best) finish(err);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout }
    );
  });
}

/** Accuracy (metres) above which a fix is likely a coarse network fix worth warning about. */
export const COARSE_ACCURACY_THRESHOLD = 200;
